import { gql } from "@apollo/client";
import { createPagePagingActions, SortDirection, TableQuery, useTableQuery, useTableQueryPaging, useTableQuerySort } from "@comet/admin";
import { Table } from "@comet/admin/lib/table/Table";
import { ContentWrapper, formatDateTimeAsString, FormattedDateWidthDateTime, useMemoizedTableVariables } from "@src/components";
import { StyledLink } from "@src/components/table/TableStyles";
import { type GQLCameraAdminQuery, type GQLlicencesAdminQuery, type GQLlicencesAdminQueryVariables } from "@src/graphql.generated";
import { type ExportVariables, loadLimit } from "@src/utils";
import { CSVExportButton } from "@src/utils/CSVExportButton";
import { useCSVExportPagedTableQuery } from "@src/utils/usePagedCSVExport";
import { type FC, type PropsWithChildren, useCallback } from "react";
import { FormattedMessage, useIntl } from "react-intl";

const licencesQuery = gql`
    query licencesAdmin($cameraId: ID!, $page: Int, $size: Int, $sort: SortInput) {
        licencesAdmin(cameraId: $cameraId, page: $page, size: $size, sort: $sort) {
            nodes {
                id
                code
                activatedAt
                package {
                    name
                }
                activeFrom
                activeTill
            }
            totalCount
            totalPages
            nextPage
            previousPage
        }
    }
`;

const PurchaseInformation: FC<PropsWithChildren<GQLCameraAdminQuery>> = ({ cameraAdmin }) => {
    const intl = useIntl();
    const pagingApi = useTableQueryPaging(1);
    const sortApi = useTableQuerySort({ columnName: "licence.activatedAt", direction: SortDirection.DESC });
    const addAdditionalQueryVariablesFn = useCallback(() => ({ cameraId: cameraAdmin.id, size: 5 }), [cameraAdmin.id]);

    const memoizedVariables = useMemoizedTableVariables<GQLlicencesAdminQueryVariables>({ pagingApi, sortApi, addAdditionalQueryVariablesFn });

    const { tableData, loading, error, api } = useTableQuery<GQLlicencesAdminQuery, GQLlicencesAdminQueryVariables>()(licencesQuery, {
        resolveTableData: ({ licencesAdmin }) => ({
            data: licencesAdmin.nodes,
            totalCount: licencesAdmin.totalCount,
            pagingInfo: createPagePagingActions(pagingApi, licencesAdmin),
        }),
        variables: memoizedVariables,
    });

    const fileName = intl.formatMessage(
        {
            id: "reviermanager.common.export.devicePurchases",
            defaultMessage: "Käufe_Gerät_{cameraId}",
        },
        { cameraId: cameraAdmin.cameraId },
    );

    const exportApi = useCSVExportPagedTableQuery<ExportVariables>(
        api,
        {
            fromPage: 1,
            toPage: tableData?.totalCount ? Math.ceil(tableData.totalCount / loadLimit) : 1,
            variablesForPage: (page) => {
                return {
                    ...memoizedVariables,
                    page,
                    size: loadLimit,
                };
            },
        },
        fileName,
    );

    return (
        <ContentWrapper title={intl.formatMessage({ id: "reviermanager.camera.purchase", defaultMessage: "Lizenzverlängerungen" })}>
            <TableQuery api={api} loading={loading} error={error}>
                {tableData && (
                    <Table
                        exportApis={[exportApi]}
                        {...tableData}
                        sortApi={sortApi}
                        rowName={intl.formatMessage(
                            {
                                id: "reviermanager.camera.purchases",
                                defaultMessage: "{count, plural, one {Lizenzverlängerung} other {Lizenzverlängerungen}}",
                            },
                            { count: tableData.totalCount },
                        )}
                        columns={[
                            {
                                name: "licence.code",
                                sortable: true,
                                header: <FormattedMessage id="reviermanager.camera.purchase.licence.code" defaultMessage="Code" />,
                                headerExcel: intl.formatMessage({ id: "reviermanager.camera.purchase.licence.code", defaultMessage: "Code" }),
                                render: ({ code, id }) => <StyledLink to={`/purchases/${id}/information`}>{code}</StyledLink>,
                            },
                            {
                                name: "name",
                                header: <FormattedMessage id="reviermanager.camera.purchase.package.name" defaultMessage="Paketname" />,
                                headerExcel: intl.formatMessage({ id: "reviermanager.camera.purchase.package.name", defaultMessage: "Paketname" }),
                                render: ({ package: { name } }) => name,
                            },
                            {
                                name: "licence.activatedAt",
                                sortable: true,
                                header: <FormattedMessage id="reviermanager.camera.purchase.package.activatedAt" defaultMessage="Aktiviert am" />,
                                headerExcel: intl.formatMessage({
                                    id: "reviermanager.camera.purchase.package.activatedAt",
                                    defaultMessage: "Aktiviert am",
                                }),
                                render: ({ activatedAt }) => activatedAt && <FormattedDateWidthDateTime value={activatedAt} />,
                                renderExcel: ({ activatedAt }) => formatDateTimeAsString(activatedAt, intl),
                            },
                        ]}
                    />
                )}
            </TableQuery>
            {tableData && <CSVExportButton exportApi={exportApi} />}
        </ContentWrapper>
    );
};
export default PurchaseInformation;
